import { Backdrop, Box, Button, Chip, Fade, Grid, LinearProgress, Modal, Paper, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useLang } from '../../../language';
import { InputText } from '../../../components/Inputs/InputText';
import { InputSelectMultiple } from '../../../components/Inputs/InputSelectMultiple';
import { useForm } from '../../../hooks/useForm';
import { EmpresasServices } from '../controllers/services/empresas.services';
import { MaestrosServices } from '../controllers/services/maestros.services';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faBuilding, faCheckCircle, faUser } from '@fortawesome/free-solid-svg-icons';

const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 720,
    maxWidth: '95%',
    maxHeight: '92vh',
    display: 'flex',
    flexDirection: 'column' as 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 3,
};

// Campos del alta. Los identificadores son los que espera
// `RegistrarEmpresaWebRequest`: los de la empresa sin prefijo y los de la persona de
// contacto con `persona_`. Los que gestiona el sistema o el panel de administración
// (`codigo`, `fecha_seguimiento`, `discriminador`, `habilitado`,
// `activar_seguimiento`, `sincronizado`, `desactivada`, `dom_validada`, `si_dom`,
// `siarc`) no aparecen: el backend los rechaza con 422 si llegan.
//
// El prefijo `persona_` es el del backend, no una clasificación del formulario. En el
// formulario solo `persona_nombre` y `persona_apellidos` son de la persona: el resto
// (dirección, teléfonos, correo, web) son datos de la empresa y se piden en su bloque.
const CAMPOS_ALTA_VACIOS = {
    identificador_fiscal: '',
    nombre: '',
    telefono: '',
    email: '',
    persona_nombre: '',
    persona_apellidos: '',
    persona_fecha_nacimiento: '',
    persona_direccion: '',
    persona_poblacion: '',
    persona_codigo_postal: '',
    persona_provincia: '',
    persona_comarca: '',
    persona_pais: '',
    persona_telefono: '',
    persona_telefono2: '',
    persona_telefono_movil: '',
    persona_telefono_fax: '',
    persona_email: '',
    persona_web: '',
    categorias: [],
    departamentos: [],
}

const DetailField = ({ label, value }: any) => (
    <Box>
        <Typography variant='caption' className='mb-0' sx={{ color: 'text.secondary' }}>
            {label}
        </Typography>
        <Typography variant='body2' className='my-0 fw-500' sx={{ wordBreak: 'break-word' }}>
            {value || '-'}
        </Typography>
    </Box>
)

// Modal "Buscar y autorizar empresa". Cuando la búsqueda no devuelve nada se ofrece
// darla de alta: el alta escribe la empresa con su persona de contacto y deja la
// solicitud de acceso del usuario de la sesión pendiente de que la empresa la acepte,
// que es exactamente el mismo punto en que queda una empresa que sí aparece en la lista.
export const ModalBuscarEmpresa = ({ open, onClose, onAutorizar, onRegistrar, registrarErrors, registrarLoading }: any) => {

    const Lang = useLang()

    const [formBusqueda, handleInputBusqueda] = useForm({
        termino: '',
    })

    const [empresaSeleccionada, setEmpresaSeleccionada] = useState<any>(null)

    // `altaAbierta` conmuta el cuerpo del modal entre la búsqueda y el formulario de
    // alta. `maestrosCargados` evita repetir los catálogos cada vez que se abre.
    const [altaAbierta, setAltaAbierta] = useState(false)
    const [maestrosCargados, setMaestrosCargados] = useState(false)

    const [formAlta, handleInputAlta, handleFormAlta, resetAlta] = useForm(CAMPOS_ALTA_VACIOS)

    const [fetchBuscarGET, fetchBuscarGETData, resetBuscarGET] = EmpresasServices.GET_BUSCAR_EMPRESA()

    const [fetchCategoriasGET, fetchCategoriasGETData] = MaestrosServices.GET_CATEGORIAS_PUBLICAS()

    const [fetchDepartamentosGET, fetchDepartamentosGETData] = MaestrosServices.GET_DEPARTAMENTOS_PUBLICAS()

    useEffect(() => {

        if (open) {

            resetBuscarGET()
            setEmpresaSeleccionada(null)
            setAltaAbierta(false)
        }

    }, [open])

    // Los catálogos solo hacen falta en el formulario de alta, así que no se piden
    // hasta que se abre.
    useEffect(() => {

        if (!altaAbierta || maestrosCargados) return

        // GET /integra-web/categorias-publicas
        fetchCategoriasGET()

        // GET /integra-web/departamentos-publicas
        fetchDepartamentosGET()

        setMaestrosCargados(true)

    }, [altaAbierta, maestrosCargados])

    const handleBuscar = (e?: any) => {

        if (e) e.preventDefault()

        setEmpresaSeleccionada(null)
        setAltaAbierta(false)

        fetchBuscarGET({
            params: {
                // El backend busca en el catálogo `integra_clientes` por el parámetro `q`
                // (CIF/identificador fiscal, código y nombre de la empresa)
                q: formBusqueda.termino,
                limit: 5,
            }
        })
    }

    const handleAbrirAlta = () => {

        // El término de búsqueda se propone como CIF: es el dato con el que se busca y
        // el que no puede repetirse, así que el usuario solo lo confirma o lo corrige.
        handleFormAlta({
            ...CAMPOS_ALTA_VACIOS,
            identificador_fiscal: (formBusqueda.termino || '').trim().toUpperCase(),
        })

        setEmpresaSeleccionada(null)
        setAltaAbierta(true)
    }

    // Los selectores múltiples no pasan por `handleInputAlta` del hook: MUI entrega el
    // type como string en React 17, así que se normaliza a número para que los ids
    // casen con los del catálogo.
    const handleChangeMultiAlta = (e: any) => {

        const { name } = e.target

        const value = e.target.value
        const ids = (Array.isArray(value) ? value : [value]).map((id: any) => parseInt(id, 10))

        handleFormAlta({ ...formAlta, [name]: ids.filter((id: any) => !isNaN(id)) })

    }

    const handleEnviarAlta = (e?: any) => {

        if (e) e.preventDefault()

        onRegistrar({
            ...formAlta,
            categorias: formAlta.categorias || [],
            departamentos: formAlta.departamentos || [],
        })
    }

    const items = fetchBuscarGETData.data?.items || []

    // El aviso de "no encontrada" solo aparece cuando la búsqueda ha respondido de
    // verdad. `useAxios` deja `data` a null hasta que llega la primera respuesta (y
    // también cuando falla), así que comprobar solo que no hay elementos y que no está
    // cargando lo mostraría de entrada, antes de que el usuario pulse buscar.
    const busquedaRealizada = !!fetchBuscarGETData.data && !fetchBuscarGETData.loading && items.length === 0

    const cargandoMaestros = !!fetchCategoriasGETData?.loading || !!fetchDepartamentosGETData?.loading

    const erroresAlta = registrarErrors || {}

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="modal-buscar-empresa"
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 200,
            }}
        >
            <Fade in={open}>

                <Grid container spacing={1} sx={style} className='z-depth-4'>
                    <Grid item xs={12}>
                        <section className='ModalHeader border-0'>
                            <Typography variant="h6" component="h2">
                                {Lang('BUSCAR_AUTORIZAR_EMPRESA')}
                            </Typography>
                        </section>
                    </Grid>

                    <Grid item xs={12}>
                        <section className='ModalBody'>

                            <Box component="form" onSubmit={handleBuscar}>

                                <Grid container spacing={1} alignItems='center'>

                                    <Grid item xs={12} sm={8}>
                                        <InputText
                                            label={Lang('BUSCAR_CIF_NOMBRE')}
                                            name='termino'
                                            value={formBusqueda.termino}
                                            onChange={handleInputBusqueda}
                                            disabled={altaAbierta}
                                        />

                                        <Typography variant='caption' className='my-0' sx={{ color: 'text.secondary' }}>
                                            {Lang('BUSCAR_EMPRESA_DESCRIPCION')}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <Button
                                            type="submit"
                                            size="small"
                                            color="primary"
                                            variant="contained"
                                            disabled={fetchBuscarGETData.loading || altaAbierta}
                                        >
                                            {Lang('REALIZAR_BUSQUEDA')}
                                        </Button>
                                    </Grid>

                                </Grid>

                            </Box>

                            {
                                fetchBuscarGETData.loading &&
                                <LinearProgress className='mt-2' />
                            }

                        </section>
                    </Grid>

                    <Grid item xs={12} className='mt-2' sx={{ flex: '1 1 auto', minHeight: 0, minWidth: 0, maxHeight: '52vh', overflowY: 'auto', overflowX: 'hidden', pr: 0.5 }}>

                        {
                            !altaAbierta &&

                            <>
                                {
                                    items.length > 0 &&

                                    <Box>
                                        <Typography variant='body2' className='my-0 mb-1' sx={{ color: 'text.secondary' }}>
                                            {Lang('SELECCIONA_UNA_EMPRESA')}
                                        </Typography>

                                        {
                                            items.map((item: any) => (
                                                <Paper
                                                    key={item.id}
                                                    className='mb-2'
                                                    onClick={() => setEmpresaSeleccionada(item)}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        border: empresaSeleccionada?.id === item.id ? '2px solid var(--color-primary)' : '1px solid rgba(0, 0, 0, 0.12)',
                                                        borderRadius: 1,
                                                    }}
                                                >
                                                    <Box className='p-2'>

                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minWidth: 0 }}>
                                                            <Typography variant='body1' className='fw-500 my-0' sx={{ wordBreak: 'break-word', minWidth: 0 }}>
                                                                {item.nombre}
                                                            </Typography>

                                                            {
                                                                item.autorizada &&
                                                                <Chip
                                                                    icon={<FontAwesomeIcon icon={faCheckCircle} />}
                                                                    label={Lang('EMPRESA_AUTORIZADA')}
                                                                    className='color-green'
                                                                    size='small'
                                                                />
                                                            }
                                                        </Box>

                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.25, mt: 0.5, minWidth: 0, wordBreak: 'break-word' }}>
                                                            <Typography variant='body2' className='my-0'>
                                                                <Box component="span" sx={{ color: 'text.secondary' }}>{Lang('CIF')}: </Box>
                                                                {item.cif}
                                                            </Typography>

                                                            {
                                                                item.email &&
                                                                <Typography variant='body2' className='my-0'>
                                                                    <Box component="span" sx={{ color: 'text.secondary' }}>{Lang('EMAIL')}: </Box>
                                                                    {item.email}
                                                                </Typography>
                                                            }

                                                            {
                                                                item.contacto &&
                                                                <Typography variant='body2' className='my-0'>
                                                                    <Box component="span" sx={{ color: 'text.secondary' }}>{Lang('CONTACTO')}: </Box>
                                                                    {item.contacto}
                                                                </Typography>
                                                            }
                                                        </Box>

                                                    </Box>
                                                </Paper>
                                            ))
                                        }
                                    </Box>
                                }

                                {
                                    busquedaRealizada &&

                                    <Paper variant="outlined" className='p-3' sx={{ textAlign: 'center' }}>

                                        <Typography variant='body1' className='fw-500 my-0'>
                                            {Lang('EMPRESA_NO_ENCONTRADA')}
                                        </Typography>

                                        <Typography variant='body2' className='my-1' sx={{ color: 'text.secondary' }}>
                                            {Lang('EMPRESA_NO_ENCONTRADA_DESCRIPCION')}
                                        </Typography>

                                        <Button
                                            color="primary"
                                            variant="contained"
                                            size="small"
                                            className='mt-1'
                                            onClick={handleAbrirAlta}
                                            startIcon={<FontAwesomeIcon icon={faBuilding} style={{ fontSize: 11 }} />}
                                        >
                                            {Lang('REGISTRAR_EMPRESA')}
                                        </Button>

                                    </Paper>
                                }

                                {
                                    empresaSeleccionada &&

                                    <Paper variant="outlined" className='p-3 mt-3'>

                                        <Typography variant='subtitle1' className='fw-500 color-primary my-0 mb-2'>
                                            {Lang('DETALLE_EMPRESA')}
                                        </Typography>

                                        <Grid container spacing={2}>

                                            <Grid item xs={12} sm={6}>
                                                <DetailField label={Lang('NOMBRE_DE_EMPRESA')} value={empresaSeleccionada.nombre} />
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <DetailField label={Lang('CIF')} value={empresaSeleccionada.cif} />
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <DetailField label={Lang('CODIGO')} value={empresaSeleccionada.codigo} />
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <DetailField label={Lang('EMAIL')} value={empresaSeleccionada.email} />
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <DetailField label={Lang('CONTACTO')} value={empresaSeleccionada.contacto} />
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <DetailField label={Lang('ESTADO')} value={empresaSeleccionada.estado} />
                                            </Grid>

                                        </Grid>

                                        <Box className='mt-4' sx={{ textAlign: 'center' }}>

                                            <Button
                                                color="primary"
                                                variant="contained"
                                                size="medium"
                                                disabled={empresaSeleccionada.autorizada}
                                                onClick={() => onAutorizar(empresaSeleccionada.id)}
                                            >
                                                {Lang('ENVIAR_AUTORIZACION')}
                                            </Button>

                                            <Typography variant='body2' className='mt-2' sx={{ color: 'text.secondary' }}>
                                                {Lang('EMPRESA_RECIBIRA_SOLICITUD')}
                                            </Typography>

                                        </Box>

                                    </Paper>
                                }
                            </>
                        }

                        {
                            altaAbierta &&

                            <Box component="form" onSubmit={handleEnviarAlta}>

                                <Paper variant="outlined" className='p-3'>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <FontAwesomeIcon icon={faBuilding} className='color-primary' />
                                        <Typography variant='subtitle1' className='fw-500 color-primary my-0'>
                                            {Lang('DATOS_EMPRESA_NUEVA')}
                                        </Typography>
                                    </Box>

                                    <Typography variant='body2' className='my-1' sx={{ color: 'text.secondary' }}>
                                        {Lang('ALTA_REQUIERE_AUTORIZACION')}
                                    </Typography>

                                    <Grid container spacing={2} className='mt-1'>

                                        <Grid item xs={12} sm={6}>
                                            <InputText
                                                label={Lang('CIF')}
                                                name='identificador_fiscal'
                                                value={formAlta.identificador_fiscal}
                                                onChange={handleInputAlta}
                                                serverErrors={erroresAlta.identificador_fiscal}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <InputText
                                                label={Lang('RAZON_SOCIAL')}
                                                name='nombre'
                                                value={formAlta.nombre}
                                                onChange={handleInputAlta}
                                                serverErrors={erroresAlta.nombre}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <InputText
                                                label={Lang('TELEFONO_PRINCIPAL')}
                                                name='telefono'
                                                value={formAlta.telefono}
                                                onChange={handleInputAlta}
                                                serverErrors={erroresAlta.telefono}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <InputText
                                                label={Lang('EMAIL_PRINCIPAL')}
                                                name='email'
                                                value={formAlta.email}
                                                onChange={handleInputAlta}
                                                serverErrors={erroresAlta.email}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <InputText
                                                label={Lang('DIRECCION')}
                                                name='persona_direccion'
                                                value={formAlta.persona_direccion}
                                                onChange={handleInputAlta}
                                                serverErrors={erroresAlta.persona_direccion}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <InputText
                                                label={Lang('POBLACION')}
                                                name='persona_poblacion'
                                                value={formAlta.persona_poblacion}
                                                onChange={handleInputAlta}
                                                serverErrors={erroresAlta.persona_poblacion}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <InputText
                                                label={Lang('CODIGO_POSTAL')}
                                                name='persona_codigo_postal'
                                                value={formAlta.persona_codigo_postal}
                                                onChange={handleInputAlta}
                                                serverErrors={erroresAlta.persona_codigo_postal}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <InputText
                                                label={Lang('PROVINCIA')}
                                                name='persona_provincia'
                                                value={formAlta.persona_provincia}
                                                onChange={handleInputAlta}
                                                serverErrors={erroresAlta.persona_provincia}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <InputText
                                                label={Lang('COMARCA')}
                                                name='persona_comarca'
                                                value={formAlta.persona_comarca}
                                                onChange={handleInputAlta}
                                                serverErrors={erroresAlta.persona_comarca}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <InputText
                                                label={Lang('PAIS')}
                                                name='persona_pais'
                                                value={formAlta.persona_pais}
                                                onChange={handleInputAlta}
                                                serverErrors={erroresAlta.persona_pais}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <InputText
                                                label={Lang('FECHA_NACIMIENTO')}
                                                name='persona_fecha_nacimiento'
                                                type='date'
                                                value={formAlta.persona_fecha_nacimiento}
                                                onChange={handleInputAlta}
                                                serverErrors={erroresAlta.persona_fecha_nacimiento}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <InputText
                                                label={Lang('TELEFONO')}
                                                name='persona_telefono'
                                                value={formAlta.persona_telefono}
                                                onChange={handleInputAlta}
                                                serverErrors={erroresAlta.persona_telefono}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <InputText
                                                label={Lang('OTRO_TELEFONO')}
                                                name='persona_telefono2'
                                                value={formAlta.persona_telefono2}
                                                onChange={handleInputAlta}
                                                serverErrors={erroresAlta.persona_telefono2}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <InputText
                                                label={Lang('TELEFONO_MOVIL')}
                                                name='persona_telefono_movil'
                                                value={formAlta.persona_telefono_movil}
                                                onChange={handleInputAlta}
                                                serverErrors={erroresAlta.persona_telefono_movil}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <InputText
                                                label={Lang('FAX')}
                                                name='persona_telefono_fax'
                                                value={formAlta.persona_telefono_fax}
                                                onChange={handleInputAlta}
                                                serverErrors={erroresAlta.persona_telefono_fax}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <InputText
                                                label={Lang('EMAIL_CONTACTO')}
                                                name='persona_email'
                                                value={formAlta.persona_email}
                                                onChange={handleInputAlta}
                                                serverErrors={erroresAlta.persona_email}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <InputText
                                                label={Lang('WEB')}
                                                name='persona_web'
                                                value={formAlta.persona_web}
                                                onChange={handleInputAlta}
                                                serverErrors={erroresAlta.persona_web}
                                            />
                                        </Grid>

                                    </Grid>

                                </Paper>

                                <Paper variant="outlined" className='p-3 mt-3'>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <FontAwesomeIcon icon={faUser} className='color-primary' />
                                        <Typography variant='subtitle1' className='fw-500 color-primary my-0'>
                                            {Lang('DATOS_CONTACTO_NUEVA')}
                                        </Typography>
                                    </Box>

                                    <Typography variant='body2' className='my-1' sx={{ color: 'text.secondary' }}>
                                        {Lang('CONTACTO_NUEVA_DESCRIPCION')}
                                    </Typography>

                                    <Grid container spacing={2} className='mt-1'>

                                        <Grid item xs={12} sm={6}>
                                            <InputText
                                                label={Lang('NOMBRE_CONTACTO')}
                                                name='persona_nombre'
                                                value={formAlta.persona_nombre}
                                                onChange={handleInputAlta}
                                                serverErrors={erroresAlta.persona_nombre}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <InputText
                                                label={Lang('APELLIDOS_CONTACTO')}
                                                name='persona_apellidos'
                                                value={formAlta.persona_apellidos}
                                                onChange={handleInputAlta}
                                                serverErrors={erroresAlta.persona_apellidos}
                                            />
                                        </Grid>

                                    </Grid>

                                </Paper>

                                <Paper variant="outlined" className='p-3 mt-3'>

                                    <Typography variant='subtitle1' className='fw-500 color-primary my-0'>
                                        {Lang('CATEGORIAS_Y_DEPARTAMENTOS')}
                                    </Typography>

                                    <Grid container spacing={2} className='mt-1'>

                                        <Grid item xs={12} sm={6}>
                                            <InputSelectMultiple
                                                label={Lang('CATEGORIAS')}
                                                name='categorias'
                                                value={formAlta.categorias}
                                                values={fetchCategoriasGETData?.data?.items || []}
                                                onChange={handleChangeMultiAlta}
                                                disabled={cargandoMaestros}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <InputSelectMultiple
                                                label={Lang('DEPARTAMENTOS')}
                                                name='departamentos'
                                                value={formAlta.departamentos}
                                                values={fetchDepartamentosGETData?.data?.items || []}
                                                onChange={handleChangeMultiAlta}
                                                disabled={cargandoMaestros}
                                            />
                                        </Grid>

                                    </Grid>

                                </Paper>

                                {
                                    cargandoMaestros &&
                                    <LinearProgress className='mt-2' />
                                }

                            </Box>
                        }

                    </Grid>

                    <Grid item xs={12}>
                        <section className='ModalFooter border-0 d-flex justify-content-between mt-2'>

                            {
                                altaAbierta
                                ?
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => { setAltaAbierta(false); resetAlta() }}
                                    startIcon={<FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: 11 }} />}
                                >
                                    {Lang('VOLVER_A_BUSQUEDA')}
                                </Button>
                                :
                                <div></div>
                            }

                            <Box sx={{ display: 'flex', gap: 1 }}>

                                {
                                    altaAbierta &&
                                    <Button
                                        size="small"
                                        color="primary"
                                        variant="contained"
                                        onClick={handleEnviarAlta}
                                        disabled={registrarLoading}
                                    >
                                        {Lang('REGISTRAR_EMPRESA')}
                                    </Button>
                                }

                                <Button size="small" variant="outlined" onClick={onClose}>
                                    {Lang('CERRAR')}
                                </Button>

                            </Box>

                        </section>
                    </Grid>
                </Grid>

            </Fade>
        </Modal>
    )
}
